import { apply, onceToday, toast } from '../core/effects';
import { S } from '../core/game';
import { m } from '../i18n';
import { sfx } from '../audio/sfx';
import { elderCard } from '../render/elderCard';
import { h } from '../ui/dom';
import { snapshot } from '../ui/snapshot';
import { popView, render } from './phone';

interface Msg {
  side: 'me' | 'them';
  text?: string;
  img?: string;
  lesson?: string;
}

/** Per-day chat log with Dad (cards, rumours, replies). */
const logs = new Map<string, Msg[]>();
let typing = false;

function dayKey(): string {
  const s = S();
  return `${s.week}:${s.dayIndex}`;
}

function todaysCard(offset = 0): { img: string; caption: string } {
  const s = S();
  const t = m().dadChat;
  const i = (s.dayIndex + s.week * 3 + offset) % t.cards.length;
  const card = t.cards[i];
  return { img: elderCard(i, card.big, card.sub), caption: t.cardCaption[i % t.cardCaption.length] };
}

/** Rumour Dad forwards today (every day but the first of the week). */
function todaysRumor(): number | null {
  const s = S();
  const n = s.dayIndex + s.week;
  return n % 3 === 0 ? null : n % m().dadChat.rumors.length;
}

function log(): Msg[] {
  const k = dayKey();
  let l = logs.get(k);
  if (!l) {
    const card = todaysCard();
    l = [
      { side: 'them', img: card.img },
      { side: 'them', text: card.caption },
    ];
    const r = todaysRumor();
    if (r !== null) l.push({ side: 'them', text: `${m().dadChat.rumorPrompt}\n${m().dadChat.rumors[r].text}` });
    logs.set(k, l);
  }
  return l;
}

function post(msg: Msg, delay = 0): void {
  if (!delay) {
    log().push(msg);
    render();
    return;
  }
  typing = true;
  render();
  window.setTimeout(() => {
    typing = false;
    log().push(msg);
    sfx('pop');
    render();
  }, delay);
}

function pick<T>(arr: readonly T[], salt: number): T {
  const s = S();
  return arr[(s.dayIndex * 7 + s.week * 3 + salt) % arr.length];
}

let replies = 0;

function reply(kind: 'thanks' | 'cardBack' | 'askDay' | 'photo'): void {
  const t = m().dadChat;
  replies++;
  if (kind === 'cardBack') {
    const c = todaysCard(3);
    post({ side: 'me', img: c.img });
  } else if (kind === 'photo') {
    const img = snapshot();
    if (img) post({ side: 'me', img });
  } else post({ side: 'me', text: t.choices[kind] });
  if (onceToday(`dad.${kind}`)) apply({ mood: 3, love: { dad: 4 } }, { float: false });
  post({ side: 'them', text: pick(t.replies[kind], replies) }, 900);
}

function rumor(r: number, correct: boolean): void {
  const t = m().dadChat.rumors[r];
  const s = S();
  if (s.daily[`rumor.${r}`]) return;
  s.daily[`rumor.${r}`] = true;
  post({ side: 'me', text: correct ? t.correct : t.share });
  if (correct) {
    apply({ stars: 5, love: { dad: 5 } });
    sfx('ok');
  } else sfx('bad');
  post({ side: 'them', text: correct ? t.dadOk : t.dadBad }, 900);
  window.setTimeout(() => post({ side: 'them', lesson: t.lesson }), 1700);
  if (!correct) toast(t.lesson);
}

function bubble(msg: Msg): HTMLElement {
  if (msg.lesson) return h('div', { class: 'lesson-card' }, h('b', null, m().social.lessonTitle), h('p', null, msg.lesson));
  return h(
    'div',
    { class: `bubble ${msg.side}${msg.img ? ' photo' : ''}` },
    msg.side === 'them' ? h('div', { class: 'from' }, m().npc.dad) : null,
    msg.img ? h('img', { src: msg.img, alt: '', class: 'chat-img' }) : msg.text,
  );
}

export function dadRoom() {
  const t = m().dadChat;
  const r = todaysRumor();
  const s = S();
  const choices: { text: string; run: () => void }[] = [
    { text: t.choices.thanks, run: () => reply('thanks') },
    { text: t.choices.cardBack, run: () => reply('cardBack') },
    { text: t.choices.askDay, run: () => reply('askDay') },
    { text: t.choices.photo, run: () => reply('photo') },
  ];
  if (r !== null && !s.daily[`rumor.${r}`]) {
    choices.unshift(
      { text: `✅ ${t.rumors[r].correct}`, run: () => rumor(r, true) },
      { text: `↪️ ${t.rumors[r].share}`, run: () => rumor(r, false) },
    );
  }
  return {
    title: t.name,
    back: popView,
    body: [
      h('div', { class: 'room-msgs' }, log().map(bubble), typing ? h('div', { class: 'bubble them typing' }, h('i'), h('i'), h('i')) : null),
      h('div', { class: 'room-choices' }, choices.map((c) => h('button', { onclick: () => (sfx('click'), c.run()) }, c.text))),
    ],
  };
}

export function dadPreview(): string {
  const l = log();
  const last = l[l.length - 1];
  return last?.text ?? m().dadChat.preview;
}
